import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { TrendingUp, BarChart3, Globe, Download } from 'lucide-react';

export default function DataExplorerPage() {
  return (
    <div className="min-h-screen py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-6">Data Explorer</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Explore comprehensive data and analytics on sugar production, trade, and market trends 
            across ACP and LDC countries.
          </p>
        </div>

        {/* Power BI Embedded */}
        <section className="mb-12">
          <Card>
            <CardHeader>
              <CardTitle>Interactive Sugar Industry Dashboard</CardTitle>
              <CardDescription>
                Explore production, trade, and market data through our interactive Power BI dashboard. 
                Use the filters and controls below to customize your view.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <p className="text-sm text-gray-600 mb-4">
                  <strong>Note:</strong> This dashboard contains real-time data from our member countries. 
                  Use the interactive controls to filter by country, time period, and product type.
                </p>
                {/* Power BI Embed */}
                <div className="w-full h-[600px] border rounded-lg bg-white">
                  <iframe
                    title="ACP Sugar Industry Data"
                    width="100%"
                    height="100%"
                    src="https://app.powerbi.com/view?r=eyJrIjoiN2EyMzViNDQtMGRlZS00Y2I2LWE2NzEtY2YwYzgwOTRjZTg4IiwidCI6IjIzOWJiOWMxLWQxZTMtNDIzNi05MjA1LWExYjkzNjVkOWM0NyJ9"
                    frameBorder="0"
                    allowFullScreen={true}
                    className="rounded-lg"
                  ></iframe>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">Production Data</span>
                <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">Export Statistics</span>
                <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded">Market Prices</span>
                <span className="text-xs bg-orange-100 text-orange-800 px-2 py-1 rounded">Employment Data</span>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Data Categories */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-8">Available Data Categories</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle>Production Statistics</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Annual sugar production by country</li>
                  <li>• Cane area harvested and yields</li>
                  <li>• Factory capacity and utilization</li>
                  <li>• Production efficiency metrics</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle>Trade Data</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Export volumes and destinations</li>
                  <li>• Import requirements and sources</li>
                  <li>• Trade balance and trends</li>
                  <li>• Market share analysis</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle>Price Information</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• International sugar prices</li>
                  <li>• Local market prices</li>
                  <li>• Price volatility analysis</li>
                  <li>• Forward market indicators</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle>Economic Impact</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• GDP contribution by country</li>
                  <li>• Employment statistics</li>
                  <li>• Foreign exchange earnings</li>
                  <li>• Investment flows</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle>Sustainability Metrics</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Environmental indicators</li>
                  <li>• Water usage efficiency</li>
                  <li>• Carbon footprint data</li>
                  <li>• Social impact measures</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle>Market Intelligence</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Global consumption trends</li>
                  <li>• Competitor analysis</li>
                  <li>• Market forecasts</li>
                  <li>• Policy impact assessments</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Data Sources and Methodology */}
        <section className="bg-gray-50 rounded-lg p-8 bg-yellow-50">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Data Sources & Methodology</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-3">Primary Sources</h3>
              <ul className="text-gray-600 space-y-1">
                <li>• Member country sugar associations</li>
                <li>• National statistical offices</li>
                <li>• Sugar mills and processing facilities</li>
                <li>• Government trade agencies</li>
                <li>• Farmer organizations and cooperatives</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-3">External Sources</h3>
              <ul className="text-gray-600 space-y-1">
                <li>• International Sugar Organization (ISO)</li>
                <li>• FAO and UN trade statistics</li>
                <li>• World Bank development indicators</li>
                <li>• Regional development banks</li>
                <li>• Market research organizations</li>
              </ul>
            </div>
          </div>
          <div className="mt-6 p-4 bg-blue-50 rounded-lg">
            <p className="text-sm text-blue-800">
              <strong>Data Quality:</strong> All data is verified through multiple sources and updated 
              regularly. Historical data spans back to 1960 where available, with monthly updates for 
              current production and trade statistics.
            </p>
          </div>
        </section>
      </div>
    </div>
  );
}